import React from "react";
import { ChevronDown } from "lucide-react";

interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
}

interface SelectTriggerProps {
  children: React.ReactNode;
  className?: string;
}

interface SelectContentProps {
  children: React.ReactNode;
}

interface SelectItemProps {
  value: string;
  children: React.ReactNode;
}

interface SelectValueProps {}

export function Select({ value, onValueChange, children }: SelectProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [displayValue, setDisplayValue] = React.useState("");
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Extract items from children to find display value
  React.useEffect(() => {
    const findValueInChildren = (children: React.ReactNode): string => {
      let result = "";
      React.Children.forEach(children, (child) => {
        if (React.isValidElement(child) && child.type === SelectContent) {
          React.Children.forEach(child.props.children, (item) => {
            if (
              React.isValidElement(item) &&
              item.type === SelectItem &&
              item.props.value === value
            ) {
              result = item.props.children as string;
            }
          });
        }
      });
      return result;
    };

    setDisplayValue(findValueInChildren(children) || value);
  }, [value, children]);

  // Handle click outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className="flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{displayValue}</span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-md">
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child) && child.type === SelectContent) {
              return React.Children.map(child.props.children, (item) => {
                if (React.isValidElement(item) && item.type === SelectItem) {
                  return (
                    <button
                      key={item.props.value}
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 hover:text-blue-900 focus:bg-blue-50 focus:text-blue-900"
                      onClick={() => {
                        onValueChange(item.props.value);
                        setIsOpen(false);
                      }}
                    >
                      {item.props.children}
                    </button>
                  );
                }
                return null;
              });
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
}

export function SelectTrigger({
  children,
  className = "",
}: SelectTriggerProps) {
  return <div className={className}>{children}</div>;
}

export function SelectContent({ children }: SelectContentProps) {
  return <>{children}</>;
}

export function SelectItem({ value, children }: SelectItemProps) {
  return <span data-value={value}>{children}</span>;
}

export function SelectValue(props: SelectValueProps) {
  return <span />;
}
